/**
 * Shared fallback for any picker whose label is derived by matching a stored id against an options
 * list that is row-level-security gated under the caller. When the assigned `value` isn't in the
 * (possibly empty, for a restricted user) list, inject a synthetic option built from the
 * server-resolved reference map so the value still renders read-only. Returns the list unchanged
 * when the value is absent, already present, or the reference yields no usable label.
 *
 * `getId` reads an option's id (lists differ: `{Id}` for profiles/orgs, `{value}` for the engagement
 * dropdown). `makeFallback` returns the synthetic option (shaped like the list's items) or null.
 * Used by every engagement reference picker — worker, stakeholder, organization, source engagement.
 */
export function withRefFallback<T>(
  options: T[],
  value: number | undefined,
  getId: (option: T) => number,
  makeFallback: (value: number) => T | null
): T[] {
  if (!value || options.some(o => getId(o) === value)) {
    return options
  }
  const fallback = makeFallback(value)
  return fallback ? [fallback, ...options] : options
}
